/**
 * Places that do something to whoever walks into them.
 *
 * A quest that only ever starts because somebody was spoken to is a quest that
 * cannot start on a beach at dawn. A trigger is a rectangle of tiles with the
 * same conditions and effects a line of conversation carries, so anything a
 * conversation can do, walking across a bridge can do too.
 *
 * The rectangle comes from what is selected in the world rather than from four
 * boxes of numbers: drawing it over the ground is how anybody would rather say
 * where it is, and the numbers are still there to be nudged afterwards.
 */

import { useEffect, useState } from 'react';
import type { ItemDefinitionWire, QuestWire } from '@inverse/protocol';
import { Conditions, Effects, type Condition, type Effect } from './Dialogue';

/** A trigger as the region file holds it. */
export interface SavedTrigger {
  id: number;
  name: string;
  tx: number;
  tz: number;
  width: number;
  height: number;
  plane: number;
  when: Condition[];
  does: Effect[];
  once: boolean;
  /**
   * Whether this one names the ground it covers.
   *
   * A town, a district, a wood. Nothing has to happen when somebody walks in:
   * the ground simply has a name, and that name goes on the map in the corner
   * and into anything that says where to go.
   */
  names?: boolean;
}

/** The patch of ground selected in the world, when there is one. */
export interface TriggerArea {
  tx: number;
  tz: number;
  width: number;
  height: number;
  plane: number;
}

export function EditorTriggers({
  triggers,
  items,
  quests,
  shops,
  area,
  onSave,
  onRemove,
  onGo,
  onRefresh,
}: {
  triggers: readonly SavedTrigger[];
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  shops: readonly { id: number; name: string }[];
  /** What is selected on the ground, which is where a new one goes. */
  area: TriggerArea | null;
  onSave: (trigger: SavedTrigger) => void;
  onRemove: (id: number) => void;
  onGo: (tx: number, tz: number) => void;
  /** Asks the server for the list, since nothing else has cause to send it. */
  onRefresh: () => void;
}): JSX.Element {
  const [chosen, setChosen] = useState<number | null>(null);
  const [draft, setDraft] = useState<SavedTrigger | null>(null);

  // Asked for once, when the tab is first drawn. Nothing else sends the list,
  // and every change to it comes back from the server on its own.
  useEffect(() => {
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = (): void => {
    const where = area ?? { tx: 0, tz: 0, width: 1, height: 1, plane: 0 };
    setChosen(null);
    setDraft({
      id: 0,
      name: '',
      tx: where.tx,
      tz: where.tz,
      width: where.width,
      height: where.height,
      plane: where.plane,
      when: [],
      does: [],
      once: true,
      names: false,
    });
  };

  if (draft) {
    return (
      <TriggerForm
        draft={draft}
        items={items}
        quests={quests}
        shops={shops}
        area={area}
        onChange={setDraft}
        onSave={() => {
          onSave(draft);
          setDraft(null);
        }}
        onCancel={() => setDraft(null)}
      />
    );
  }

  const open = triggers.find((trigger) => trigger.id === chosen) ?? null;

  return (
    <>
      <button type="button" className="wide primary" onClick={start}>
        {area ? `New one over ${area.width} by ${area.height} tiles` : 'New one'}
      </button>
      <p className="hint">
        {area
          ? `Starting at ${area.tx}, ${area.tz}. Select more ground to make it wider.`
          : 'Nothing is selected, so a new one covers a single tile until it is moved.'}
      </p>

      <div className="editor-list">
        {triggers.map((trigger) => (
          <button
            key={trigger.id}
            type="button"
            className={trigger.id === chosen ? 'editor-row on' : 'editor-row'}
            onClick={() => {
              setChosen(trigger.id);
              onGo(trigger.tx, trigger.tz);
            }}
          >
            <span className="editor-row-name">{trigger.name || `Place ${trigger.id}`}</span>
            <span className="editor-row-note">
              {trigger.width} x {trigger.height}
            </span>
          </button>
        ))}
      </div>

      {triggers.length === 0 ? <p className="hint">Nothing here does anything yet.</p> : null}

      {open ? (
        <div className="editor-facts">
          <h3>{open.name || `Place ${open.id}`}</h3>
          <p className="hint">
            At {open.tx}, {open.tz} on plane {open.plane}. {open.does.length} things happen
            {open.when.length > 0 ? `, if ${open.when.length} hold` : ''}.
            {open.once ? ' Once each.' : ' Every time.'}
          </p>

          <button type="button" className="wide primary" onClick={() => setDraft({ ...open })}>
            Edit
          </button>
          <button type="button" className="wide danger" onClick={() => onRemove(open.id)}>
            Delete
          </button>
        </div>
      ) : null}
    </>
  );
}

function TriggerForm({
  draft,
  items,
  quests,
  shops,
  area,
  onChange,
  onSave,
  onCancel,
}: {
  draft: SavedTrigger;
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  shops: readonly { id: number; name: string }[];
  area: TriggerArea | null;
  onChange: (draft: SavedTrigger) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element {
  const set = (changed: Partial<SavedTrigger>): void => onChange({ ...draft, ...changed });

  const whole = (text: string, least: number): number => {
    const held = Math.round(Number(text));
    return Number.isFinite(held) ? Math.max(least, held) : least;
  };

  return (
    <>
      <h3>{draft.name || (draft.id > 0 ? `Place ${draft.id}` : 'A new place')}</h3>

      <label className="editor-field">
        <span>Name</span>
        <input
          type="text"
          value={draft.name}
          placeholder="The bridge"
          onChange={(event) => set({ name: event.target.value.slice(0, 48) })}
        />
      </label>

      <div className="tool-row">
        <label className="editor-number">
          <span>X</span>
          <input
            type="number"
            value={draft.tx}
            onChange={(event) => set({ tx: whole(event.target.value, 0) })}
          />
        </label>
        <label className="editor-number">
          <span>Z</span>
          <input
            type="number"
            value={draft.tz}
            onChange={(event) => set({ tz: whole(event.target.value, 0) })}
          />
        </label>
        <label className="editor-number">
          <span>Width</span>
          <input
            type="number"
            value={draft.width}
            onChange={(event) => set({ width: Math.min(128, whole(event.target.value, 1)) })}
          />
        </label>
        <label className="editor-number">
          <span>Height</span>
          <input
            type="number"
            value={draft.height}
            onChange={(event) => set({ height: Math.min(128, whole(event.target.value, 1)) })}
          />
        </label>
      </div>

      {area ? (
        <button
          type="button"
          className="wide"
          title="Puts it over the ground selected in the world"
          onClick={() =>
            set({
              tx: area.tx,
              tz: area.tz,
              width: area.width,
              height: area.height,
              plane: area.plane,
            })
          }
        >
          Take the selected ground
        </button>
      ) : null}

      {/* The second job named ground does: being called something. A place can
          do both, and most do only one. */}
      <div className="tool-row">
        <span className="tool-label">Names it</span>
        <button
          type="button"
          className={draft.names === true ? 'editor-tab on' : 'editor-tab'}
          title="The ground here is called this, on the map and in anything that says where to go"
          onClick={() => set({ names: draft.names !== true })}
        >
          {draft.names === true ? 'Called this' : 'Unnamed ground'}
        </button>
      </div>

      <div className="tool-row">
        <span className="tool-label">Happens</span>
        <button
          type="button"
          className={draft.once ? 'editor-tab on' : 'editor-tab'}
          onClick={() => set({ once: true })}
        >
          Once each
        </button>
        <button
          type="button"
          className={draft.once ? 'editor-tab' : 'editor-tab on'}
          onClick={() => set({ once: false })}
        >
          Every time
        </button>
      </div>

      <h4>Conditions</h4>
      <Conditions
        held={draft.when}
        items={items}
        quests={quests}
        onChange={(when) => set({ when })}
      />
      <p className="hint">Nothing here means it happens to anybody who walks in.</p>

      <h4>Effects</h4>
      <Effects
        held={draft.does}
        items={items}
        quests={quests}
        shops={shops}
        onChange={(does) => set({ does })}
      />

      <button type="button" className="wide primary" onClick={onSave}>
        Keep it
      </button>
      <button type="button" className="wide" onClick={onCancel}>
        Back to the list
      </button>
    </>
  );
}
